import { useState, useRef } from "react";
import {
    Box,
    TableSortLabel,
    IconButton,
    Button,
    Tooltip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";

const SelectFilterableHeaderCell = ({
    label,
    columnKey,
    sortConfig,
    onSortChange,
    filterValue = [],
    onFilterChange,
    options = [],
    disableFilter = false
}) => {
    const isActive = sortConfig?.key === columnKey;
    const anchorRef = useRef(null);
    const [openFilter, setOpenFilter] = useState(false);
    const [tempFilter, setTempFilter] = useState(filterValue);

    const handleSortClick = () => {
        const newDirection =
            isActive && sortConfig.direction === "asc" ? "desc" : "asc";
        onSortChange({ key: columnKey, direction: newDirection });
    };

    const handleFilterIconClick = () => {
        setTempFilter(filterValue);
        setOpenFilter((prev) => !prev);
    };

    const handleApplyFilter = () => {
        onFilterChange(columnKey, tempFilter);
        setOpenFilter(false);
    };

    const handleMenuClose = () => {
        setOpenFilter(false);
    };

    const sortedOptions = [...options].sort((a, b) => {
        if (typeof a === "string" && typeof b === "string") {
            return a.localeCompare(b);
        }
        return a > b ? 1 : a < b ? -1 : 0;
    });

    return (
        <Box display="flex" alignItems="center" gap={0.5}>
            <Box
                fontWeight="bold"
                sx={{
                    textTransform: "capitalize",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
                onClick={handleSortClick}
            >
                {label}
            </Box>

            <TableSortLabel
                active={isActive}
                direction={isActive ? sortConfig.direction : "asc"}
                onClick={handleSortClick}
                sx={{ cursor: "pointer" }}
            />

            {!disableFilter && (
                <>
                    <Tooltip title={`Filter by ${label}`}>
                        <IconButton
                            size="small"
                            ref={anchorRef}
                            onClick={handleFilterIconClick}
                            sx={{ padding: 0 }}
                        >
                            <FilterListIcon
                                fontSize="small"
                                sx={{
                                    color: filterValue.length
                                        ? "primary.main"
                                        : "inherit",
                                }}
                            />
                        </IconButton>
                    </Tooltip>

                    <Popover
                        open={openFilter}
                        anchorEl={anchorRef.current}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        disableAutoFocus
                        disableEnforceFocus
                        PaperProps={{
                            sx: {
                                p: 1,
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                whiteSpace: "nowrap",
                                maxWidth: "none",
                                boxShadow: 3,
                            },
                        }}
                    >
                    <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={sortedOptions}
                            value={tempFilter}
                            onChange={(_, newValue) => setTempFilter(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="Filter..."
                                    InputProps={{
                                        ...params.InputProps,
                                        disableUnderline: false,
                                        sx: {
                                            fontFamily: "Roboto",
                                            fontWeight: 400,
                                            fontSize: 10,
                                            letterSpacing: 0,
                                            verticalAlign: "middle",
                                            color: "#454545",
                                        },
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        style: {
                                            fontFamily: "Roboto",
                                            fontWeight: 400,
                                            fontSize: 10,
                                            letterSpacing: 0,
                                            verticalAlign: "middle",
                                            color: "#454545",
                                        },
                                    }}
                                />
                            )}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} style={{
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: 10,
                                    letterSpacing: 0,
                                    verticalAlign: "middle",
                                    color: "#454545",
                                    paddingTop: "2px"
                                }}>
                                    {option}
                                </li>
                            )}
                            size="small"
                            sx={{ width: 200 }}
                            openOnFocus
                            MenuProps={{
                                disablePortal: true,
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                getContentAnchorEl: null,
                            }}
                        />

                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleApplyFilter}
                            sx={{
                                minWidth: 60,
                                textTransform: "none",
                                margin: "0 8px",
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: 10,
                                letterSpacing: 0,
                                verticalAlign: "middle",
                                color: "#454545",
                            }}
                        >
                            Apply
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setTempFilter([]);
                                onFilterChange(columnKey, []);
                                setOpenFilter(false);
                            }}
                            sx={{
                                minWidth: 60,
                                textTransform: "none",
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: 10,
                                letterSpacing: 0,
                                verticalAlign: "middle",
                                color: "#454545",
                            }}
                        >
                            Clear
                        </Button>
                    </Popover>
                </>
            )}
        </Box>
    );
};

export default SelectFilterableHeaderCell;